// Admin Channels (Broadcast destinations) — '/admin/channels'.
//
// Ports the legacy `Channels` React component from
// www/livevane.com/htdocs/admin/index.html (~lines 2034–2336, rendered
// inside `<SectionCard title="Broadcast Channels" />` at legacy line
// 2100) into a TanStack file-based route under the /admin layout. The
// legacy admin stays in place until the full parity checklist ships;
// this is the `Channels` row from
// apps/web/src/routes/admin/PARITY.md.
//
// Auth gate:
//   NOT re-added in this file. The parent layout at
//   apps/web/src/routes/admin/route.tsx already throws-redirects to
//   /login when `token === null || user?.role !== 'admin'` inside its
//   beforeLoad. Child routes only handle their own data loading.
//
// API endpoints (verified against the legacy admin and the backing PHP):
//   - GET  /api/system/channels/                          → list channels
//     (mirror of `www/livevane.com/api/system/channels/index.php`).
//   - POST /api/system/channels/ { label, url }           → create.
//     (No `action` key on the add path — matches legacy line 3948.)
//   - POST /api/system/channels/ { action:'toggle', id }  → toggle enabled
//     (matches legacy line 3936; key is `id`, not `label`).
//   - POST /api/system/channels/ { action:'delete', id }  → delete
//     (POST, NOT DELETE — matches legacy line 3961).
//
//   IMPORTANT: there is NO `/api/channels/` endpoint and no nginx
//   rewrite aliases it (see nginx/sites-available/livevane.conf:91-108
//   and PARITY.md's "No nginx aliases for API paths" constraint).
//   All channel HTTP must go through `/system/channels/`.
//
// View-as impersonation:
//   Every channels request is routed through `scopedApiPath(base,
//   viewAsId)` so a super-admin viewing as another user automatically
//   threads `?view_as=<id>` onto the URL. The legacy admin does the
//   same at line ~3720. The UI input for this value lives on the Users
//   screen (`apps/web/src/routes/admin/users.tsx`) per PARITY.md; here
//   we plumb the helper through every call site so flipping the local
//   `viewAsId` (or wiring a future context provider) is sufficient to
//   re-key all channel queries against another admin id. Mirrors the
//   same plumbing pattern already used by `routes/admin/streams.tsx`,
//   `routes/admin/recordings.tsx`, and `routes/admin/settings.tsx`.
//
// Security / conventions:
//   - All HTTP through `apiClient` (no raw fetch, no legacy
//     authHeaders()). The bearer token never leaves api-client.ts.
//   - `toFormDataPhp` is used for every POST so the PHP layer can
//     read the body via `$_POST` (it also accepts JSON, but FormData
//     matches the rest of the SPA).
//   - Delete is gated behind a shadcn Dialog instead of the legacy
//     `confirm()` prompt at legacy line 2155.
//
// UI:
//   - `SectionCard title="Broadcast destinations"` wraps a shadcn Table
//     at `md` and up, and stacked two-line rows below it. The legacy URL
//     secret-masking is preserved on the URL cell; a Copy button copies
//     the full ingest URL.
//   - The "Add Channel" form is inline below the table, gated by a
//     "+ Add Channel" toggle like the legacy `showAdd` state. The
//     legacy platform-icon picker grid (legacy 2169–2199) is restored
//     here as a DropdownMenu on the Add Channel button — picking a
//     preset opens the form with a Stream Key input pre-bound to the
//     canonical RTMP base; Custom keeps the legacy free-text URL form.

import { useRef, useState, type FormEvent } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Copy } from 'lucide-react'

import { InlineError } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SectionCard } from '@/components/ui/section-card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ResponsiveRows,
  StackedRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import {
  apiClient,
  ApiError,
  scopedApiPath,
  toFormDataPhp,
} from '@/lib/api-client'
import { useViewAsStore } from '@/stores/view-as-store'
import type {
  BroadcastChannel,
  ChannelMutationResponse,
  ChannelsResponse,
} from '@/lib/types'

export const Route = createFileRoute('/admin/channels')({
  component: AdminChannelsPage,
})

// Shared react-query key prefix. The active viewAsId (or '' when none)
// is appended at the call site so a future super-admin impersonation
// flip re-keys the query and forces a refetch against the scoped path,
// rather than serving the previous admin's cached channel list.
const QK_CHANNELS_PREFIX = ['admin', 'channels'] as const

// Platform presets surfaced by the "+ Add Channel" dropdown. The
// `urlBase` values are the canonical RTMP ingest URLs for each
// platform; the user-supplied stream key is appended to the base
// verbatim to build the final URL stored on the channel row.
//
// IMPORTANT: these host strings MUST stay aligned with the substrings
// matched by `StreamProbe::detectPlatform` (see
// `tests/encoder/StreamProbePlatformTest.php`) — the PHP backend
// auto-detects platform/max_bitrate from the URL host, so changing
// these breaks platform detection on the server. The frontend does
// not need to know about the platform after submit; the addChannel
// mutation still posts `{ label, url }` unchanged.
type PlatformKey =
  | 'twitch'
  | 'youtube'
  | 'facebook'
  | 'kick'
  | 'trovo'
  | 'tiktok'
  | 'custom'

interface PlatformPreset {
  key: PlatformKey
  label: string
  urlBase: string | null
}

const PLATFORM_PRESETS: ReadonlyArray<PlatformPreset> = [
  { key: 'twitch', label: 'Twitch', urlBase: 'rtmp://live.twitch.tv/app/' },
  {
    key: 'youtube',
    label: 'YouTube',
    urlBase: 'rtmp://a.rtmp.youtube.com/live2/',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    urlBase: 'rtmps://live-api-s.facebook.com:443/rtmp/',
  },
  { key: 'kick', label: 'Kick', urlBase: 'rtmps://ingest.kick.com:443/live/' },
  { key: 'trovo', label: 'Trovo', urlBase: 'rtmp://livepush.trovo.live/live/' },
  { key: 'tiktok', label: 'TikTok', urlBase: 'rtmp://push.tiktok.com/live/' },
  { key: 'custom', label: 'Custom', urlBase: null },
]

function findPreset(key: PlatformKey | null): PlatformPreset | null {
  if (key === null) return null
  return PLATFORM_PRESETS.find((p) => p.key === key) ?? null
}

/**
 * Mask the trailing path segment of an RTMP URL the same way the legacy
 * admin does at line ~2137 (`ch.url?.replace(/\/[^/]+$/, "/••••••••")`).
 * Stream keys live in that final segment, so we never echo them into
 * the DOM verbatim.
 */
function maskStreamUrl(url: string): string {
  if (url.length === 0) return url
  return url.replace(/\/[^/]+$/, '/••••••••')
}

function isTwitchChannel(channel: BroadcastChannel): boolean {
  return channel.platform?.toLowerCase() === 'twitch'
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unknown error.'
}

function AdminChannelsPage() {
  const queryClient = useQueryClient()

  // View-as impersonation is driven by the dropdown in the admin
  // layout header (apps/web/src/routes/admin/route.tsx). Selecting a
  // different admin re-keys every query on this page because viewAsId
  // is part of the queryKey below.
  const viewAsId = useViewAsStore((s) => s.viewAsId)
  const buildPath = (base: string): string => scopedApiPath(base, viewAsId)
  const viewAsKey: string = viewAsId == null ? '' : String(viewAsId)
  const queryKey = [...QK_CHANNELS_PREFIX, viewAsKey] as const

  // Add-form local state. `addPlatform` selects which preset is being
  // added (or null when the form is closed); presets bind to a Stream
  // Key field that is appended to the preset URL base, while 'custom'
  // keeps the legacy free-text URL field for non-canonical RTMP
  // destinations.
  const [showAdd, setShowAdd] = useState<boolean>(false)
  const [addPlatform, setAddPlatform] = useState<PlatformKey | null>(null)
  const [newLabel, setNewLabel] = useState<string>('')
  const [newStreamKey, setNewStreamKey] = useState<string>('')
  const [newUrl, setNewUrl] = useState<string>('')
  const [newTwitchChatChannel, setNewTwitchChatChannel] = useState<string>('')

  // Delete confirmation dialog state — replaces the legacy
  // window.confirm() at legacy line 2155.
  const [pendingDelete, setPendingDelete] = useState<BroadcastChannel | null>(
    null,
  )
  // Disable confirmation: a relay may be carrying a live stream.
  const [pendingDisable, setPendingDisable] = useState<BroadcastChannel | null>(
    null,
  )

  // Edit dialog state — derived `open` from `editingChannel !== null`,
  // matching the delete dialog idiom. `editLabel`/`editStreamKey` are
  // local input mirrors split from the row's stored URL via
  // `lastIndexOf('/')` so the user can edit either independently
  // without exposing the URL host/prefix as a free-text field.
  const [editingChannel, setEditingChannel] =
    useState<BroadcastChannel | null>(null)
  const [editLabel, setEditLabel] = useState<string>('')
  const [editStreamKey, setEditStreamKey] = useState<string>('')
  const [editTwitchChatChannel, setEditTwitchChatChannel] = useState<string>('')

  const [inlineError, setInlineError] = useState<string | null>(null)

  const channelsQuery = useQuery({
    queryKey,
    queryFn: () =>
      apiClient.get<ChannelsResponse>(buildPath('/system/channels/')),
    select: (data: ChannelsResponse): BroadcastChannel[] => data.channels,
  })

  const writeBack = (data: ChannelMutationResponse): void => {
    // The PHP layer returns the full channel list on every successful
    // mutation; fold it back into the cache directly so we don't need a
    // round-trip refetch. Fall back to an invalidation if it's missing.
    if (data.channels !== undefined) {
      queryClient.setQueryData<ChannelsResponse>(queryKey, {
        success: true,
        channels: data.channels,
      })
    } else {
      void queryClient.invalidateQueries({ queryKey })
    }
  }

  const addChannel = useMutation({
    mutationFn: (input: {
      label: string
      url: string
      twitch_chat_channel?: string
    }) =>
      // Add path: no `action` key. Matches legacy admin line 3948 and the
      // PHP fallback at api/system/channels/index.php:59 which defaults
      // to `action = 'create'` when only label+url are present. The URL
      // is threaded through scopedApiPath so view-as impersonation —
      // when the super-admin is acting on behalf of another admin —
      // creates the channel under the target's account, not the
      // session admin's.
      apiClient.post<ChannelMutationResponse>(
        buildPath('/system/channels/'),
        toFormDataPhp({
          label: input.label,
          url: input.url,
          twitch_chat_channel: input.twitch_chat_channel,
        }),
      ),
    onSuccess: (data) => {
      setInlineError(null)
      setNewLabel('')
      setNewStreamKey('')
      setNewUrl('')
      setNewTwitchChatChannel('')
      setAddPlatform(null)
      setShowAdd(false)
      writeBack(data)
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  const toggleChannel = useMutation({
    mutationFn: (id: number) =>
      // Toggle path: legacy admin line 3936 — POST with {action:'toggle', id}.
      // scopedApiPath ensures the toggle hits the impersonated admin's
      // channel row when view-as is active.
      apiClient.post<ChannelMutationResponse>(
        buildPath('/system/channels/'),
        toFormDataPhp({ action: 'toggle', id }),
      ),
    onSuccess: (data) => {
      setInlineError(null)
      writeBack(data)
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  const deleteChannel = useMutation({
    mutationFn: (id: number) =>
      // Delete path: legacy admin line 3961 — POST (NOT DELETE) with
      // {action:'delete', id}. The PHP layer only routes deletes on POST.
      // scopedApiPath threads view_as so a super-admin deletes the
      // impersonated admin's row, not their own.
      apiClient.post<ChannelMutationResponse>(
        buildPath('/system/channels/'),
        toFormDataPhp({ action: 'delete', id }),
      ),
    onSuccess: (data) => {
      setInlineError(null)
      writeBack(data)
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  // Update path: POST {action:'update', id, label?, url?}. The backend
  // accepts partial updates — only the fields included in the FormData
  // are written, so we never overwrite a key the user did not change.
  // Bitrate (`custom_bitrate`) is intentionally NOT exposed here; the
  // dialog only edits label + stream key, and the backend continues to
  // manage bitrate via its own preserve-on-update path.
  const updateChannel = useMutation({
    mutationFn: (input: {
      id: number
      label?: string
      url?: string
      twitch_chat_channel?: string
    }) =>
      apiClient.post<ChannelMutationResponse>(
        buildPath('/system/channels/'),
        toFormDataPhp({
          action: 'update',
          id: input.id,
          ...(input.label !== undefined ? { label: input.label } : {}),
          ...(input.url !== undefined ? { url: input.url } : {}),
          ...(input.twitch_chat_channel !== undefined
            ? { twitch_chat_channel: input.twitch_chat_channel }
            : {}),
        }),
      ),
    onSuccess: (data) => {
      setInlineError(null)
      setEditingChannel(null)
      setEditLabel('')
      setEditStreamKey('')
      setEditTwitchChatChannel('')
      writeBack(data)
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  const handleAddSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (addPlatform === null || addChannel.isPending) return
    const label = newLabel.trim()
    if (label === '') return
    const preset = findPreset(addPlatform)
    if (preset === null) return
    if (preset.urlBase === null) {
      // Custom: behave like the legacy free-text form.
      const url = newUrl.trim()
      if (url === '') return
      addChannel.mutate({ label, url })
      return
    }
    // Preset: build the full URL by concatenating the canonical base
    // with the stream key. Do NOT trim a leading slash off the key —
    // the path regex in Channel.php accepts `[a-zA-Z0-9._~:/-]`, and
    // the PHP `sanitize_channel` step handles further cleanup.
    const streamKey = newStreamKey.trim()
    if (streamKey === '') return
    const url = preset.urlBase + streamKey
    addChannel.mutate({
      label,
      url,
      ...(preset.key === 'twitch'
        ? { twitch_chat_channel: newTwitchChatChannel.trim() }
        : {}),
    })
  }

  const openAddForm = (key: PlatformKey): void => {
    setAddPlatform(key)
    setNewLabel('')
    setNewStreamKey('')
    setNewUrl('')
    setNewTwitchChatChannel('')
    setInlineError(null)
    setShowAdd(true)
  }

  const cancelAddForm = (): void => {
    setShowAdd(false)
    setAddPlatform(null)
    setNewLabel('')
    setNewStreamKey('')
    setNewUrl('')
    setNewTwitchChatChannel('')
  }

  const confirmDelete = (): void => {
    if (pendingDelete === null) return
    deleteChannel.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
    })
  }

  // Focus returns to the row button that opened a confirmation.
  const confirmTriggerRef = useRef<HTMLElement | null>(null)

  const copyUrl = (url: string): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(url)
    }
  }

  const channels: BroadcastChannel[] = channelsQuery.data ?? []
  const activePreset = findPreset(addPlatform)
  const isCustom = activePreset?.urlBase === null
  const addDisabled =
    addPlatform === null ||
    activePreset === null ||
    newLabel.trim() === '' ||
    (isCustom ? newUrl.trim() === '' : newStreamKey.trim() === '') ||
    addChannel.isPending

  const isBusy = (channel: BroadcastChannel): boolean =>
    (toggleChannel.isPending && toggleChannel.variables === channel.id) ||
    (deleteChannel.isPending && deleteChannel.variables === channel.id) ||
    (updateChannel.isPending && updateChannel.variables?.id === channel.id)

  const openEdit = (channel: BroadcastChannel): void => {
    setEditingChannel(channel)
    setEditLabel(channel.label)
    setEditStreamKey(channel.url.slice(channel.url.lastIndexOf('/') + 1))
    setEditTwitchChatChannel(channel.twitch_chat_channel ?? '')
    setInlineError(null)
  }

  const platformName = (channel: BroadcastChannel): string => {
    const key = channel.platform?.toLowerCase() ?? ''
    return PLATFORM_PRESETS.find((p) => p.key === key)?.label ?? (key === '' ? 'Custom RTMP' : channel.platform ?? 'Custom RTMP')
  }

  const rowActions = (channel: BroadcastChannel) => {
    const busy = isBusy(channel)
    return (
      <>
        {isTwitchChannel(channel) && (
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/chat" search={{ channel: channel.id }} data-testid="admin-channels-chat">Open chat</Link>
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="admin-channels-toggle"
          disabled={busy}
          onClick={(e) => {
            // Disabling stops a relay that may be carrying a live stream,
            // so it is confirmed; enabling is not.
            if (channel.enabled) {
              confirmTriggerRef.current = e.currentTarget
              setPendingDisable(channel)
            } else {
              toggleChannel.mutate(channel.id)
            }
          }}
        >
          {channel.enabled ? 'Disable' : 'Enable'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="admin-channels-edit"
          disabled={busy}
          onClick={() => openEdit(channel)}
        >
          Edit
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          data-testid="admin-channels-delete"
          disabled={busy}
          onClick={(e) => {
            confirmTriggerRef.current = e.currentTarget
            setPendingDelete(channel)
          }}
        >
          Delete
        </Button>
      </>
    )
  }

  const statePill = (channel: BroadcastChannel) => (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        channel.enabled
          ? 'border-cyber-cyan/40 bg-cyber-cyan/10 text-cyber-cyan'
          : 'border-border/60 bg-muted/40 text-muted-foreground',
      )}
    >
      {channel.enabled ? 'Enabled' : 'Disabled'}
    </span>
  )

  const urlCell = (channel: BroadcastChannel) => (
    <span className="flex min-w-0 items-center gap-1">
      <span className="min-w-0 truncate font-mono text-xs text-muted-foreground" title={maskStreamUrl(channel.url)}>
        {maskStreamUrl(channel.url)}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 shrink-0 p-0"
        aria-label={`Copy ingest URL for ${channel.label}`}
        title="Copy ingest URL"
        onClick={() => copyUrl(channel.url)}
      >
        <Copy aria-hidden="true" className="size-3.5" />
      </Button>
    </span>
  )

  const addMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" size="sm" data-testid="admin-channels-add-open">
          Add destination
          <ChevronDown className="size-4 opacity-70" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {PLATFORM_PRESETS.map((preset) => (
          <DropdownMenuItem
            key={preset.key}
            data-testid={`admin-channels-add-platform-${preset.key}`}
            onSelect={() => openAddForm(preset.key)}
          >
            {preset.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div data-testid="admin-channels" className="space-y-4">
      <SectionCard
        title="Broadcast destinations"
        description="Where live streams are relayed. Disabling a destination stops relaying to it; viewers on other destinations are unaffected."
      >
        <div className="space-y-4">
          {!showAdd ? <div className="flex justify-end">{addMenu}</div> : null}

          {inlineError !== null && (
            <InlineError message={inlineError} data-testid="admin-channels-error" />
          )}

          {channelsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : channelsQuery.isError ? (
            <InlineError message="Couldn’t load destinations. Try refreshing." />
          ) : channels.length === 0 ? (
            <div
              data-testid="admin-channels-empty"
              className="rounded-md border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground"
            >
              No destinations yet. Add one to relay live streams to Twitch, YouTube or a custom RTMP server.
            </div>
          ) : (
            <ResponsiveRows
              table={
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-full">Destination</TableHead>
                      <TableHead className="min-w-[14rem]">Ingest URL</TableHead>
                      <TableHead>Twitch chat</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {channels.map((channel) => (
                      <TableRow
                        key={channel.id}
                        data-testid="admin-channels-row"
                        data-channel-id={channel.id}
                        className={isBusy(channel) ? 'opacity-60' : undefined}
                      >
                        <TableCell className="min-w-[12rem]">
                          <div className="truncate font-medium text-foreground" title={channel.label}>
                            {channel.label}
                          </div>
                          <div className="text-xs text-muted-foreground">{platformName(channel)}</div>
                        </TableCell>
                        <TableCell className="max-w-[18rem]">{urlCell(channel)}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {isTwitchChannel(channel)
                            ? (channel.twitch_chat_channel ?? 'Not set')
                            : '—'}
                        </TableCell>
                        <TableCell>{statePill(channel)}</TableCell>
                        <TableCell className="w-px whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">{rowActions(channel)}</div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              }
              stacked={
                <StackedRows
                  rows={channels}
                  rowKey={(c) => c.id}
                  rowProps={(c) => ({
                    'data-testid': 'admin-channels-row',
                    'data-channel-id': c.id,
                    className: isBusy(c) ? 'opacity-60' : undefined,
                  })}
                  primary={(c) => (
                    <>
                      <div className="truncate" title={c.label}>{c.label}</div>
                      <div className="text-xs font-normal text-muted-foreground">{platformName(c)}</div>
                    </>
                  )}
                  secondary={statePill}
                  fields={[
                    { label: 'URL', value: urlCell },
                    {
                      label: 'Twitch chat',
                      value: (c) => c.twitch_chat_channel ?? 'Not set',
                      show: isTwitchChannel,
                    },
                  ]}
                  actions={rowActions}
                />
              }
            />
          )}

          {/* Inline add form — opened from the "Add destination" menu. */}
          {showAdd && activePreset !== null ? (
            <form
              onSubmit={handleAddSubmit}
              data-testid="admin-channels-add-form"
              data-add-platform={activePreset.key}
              className="space-y-3 rounded-md border border-border/60 bg-background/40 p-4"
            >
              <h3 className="text-sm font-semibold text-foreground">
                {activePreset.urlBase === null
                  ? 'Add a custom RTMP destination'
                  : `Add a ${activePreset.label} destination`}
              </h3>
              {activePreset.urlBase !== null && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Ingest URL</Label>
                  <div
                    data-testid="admin-channels-url-base"
                    className="rounded-md border border-border/60 bg-background/60 px-3 py-2 font-mono text-xs text-muted-foreground"
                  >
                    {activePreset.urlBase}
                  </div>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="admin-channels-label">Name</Label>
                  <Input
                    id="admin-channels-label"
                    data-testid="admin-channels-label"
                    type="text"
                    autoComplete="off"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder={
                      activePreset.urlBase === null
                        ? 'e.g. Backup relay'
                        : `e.g. ${activePreset.label} main`
                    }
                  />
                </div>
                {activePreset.urlBase === null ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="admin-channels-url">URL</Label>
                    <Input
                      id="admin-channels-url"
                      data-testid="admin-channels-url"
                      type="text"
                      autoComplete="off"
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      placeholder="rtmp://server.example.com/app/stream_key"
                    />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label htmlFor="admin-channels-stream-key">Stream key</Label>
                    <Input
                      id="admin-channels-stream-key"
                      data-testid="admin-channels-stream-key"
                      type="text"
                      autoComplete="off"
                      value={newStreamKey}
                      onChange={(e) => setNewStreamKey(e.target.value)}
                      placeholder="live_123456_abcdef"
                    />
                  </div>
                )}
              </div>
              {activePreset.key === 'twitch' && (
                <div className="space-y-1.5">
                  <Label htmlFor="admin-channels-twitch-chat">Twitch chat channel</Label>
                  <Input
                    id="admin-channels-twitch-chat"
                    data-testid="admin-channels-twitch-chat"
                    type="text"
                    autoComplete="off"
                    value={newTwitchChatChannel}
                    onChange={(e) => setNewTwitchChatChannel(e.target.value)}
                    placeholder="channel_login"
                  />
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="submit"
                  data-testid="admin-channels-add"
                  disabled={addDisabled}
                >
                  {addChannel.isPending ? 'Adding…' : 'Add destination'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={cancelAddForm}
                  data-testid="admin-channels-add-cancel"
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : null}
        </div>
      </SectionCard>

      <ConfirmDialog
        open={pendingDisable !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDisable(null)
        }}
        title={pendingDisable ? `Disable “${pendingDisable.label}”?` : ''}
        description="Relaying to this destination stops immediately for any stream that is live. You can enable it again at any time."
        confirmLabel="Disable"
        busyLabel="Disabling…"
        confirmVariant="destructive-solid"
        busy={toggleChannel.isPending}
        returnFocusTo={confirmTriggerRef}
        onConfirm={() => {
          if (!pendingDisable) return
          toggleChannel.mutate(pendingDisable.id, {
            onSuccess: () => setPendingDisable(null),
          })
        }}
        data-testid="admin-channels-disable-confirm"
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
        title={pendingDelete ? `Delete “${pendingDelete.label}”?` : 'Delete destination?'}
        description={
          pendingDelete?.enabled
            ? 'Relaying to this destination stops immediately for any stream that is live, and the destination is removed. This can’t be undone.'
            : 'The destination and its ingest URL are removed. This can’t be undone.'
        }
        confirmLabel="Delete destination"
        busyLabel="Deleting…"
        confirmVariant="destructive-solid"
        busy={deleteChannel.isPending}
        error={pendingDelete ? inlineError : null}
        returnFocusTo={confirmTriggerRef}
        onConfirm={confirmDelete}
        data-testid="admin-channels-confirm"
      />

      {/* Edit destination — independent edits for the name and the trailing
          URL stream-key segment. Split via `lastIndexOf('/')` so the
          read-only base prefix can be shown verbatim. */}
      <Dialog
        open={editingChannel !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingChannel(null)
            setEditLabel('')
            setEditStreamKey('')
            setEditTwitchChatChannel('')
          }
        }}
      >
        <DialogContent data-testid="admin-channels-edit-dialog">
          {editingChannel !== null && (() => {
            const splitIdx = editingChannel.url.lastIndexOf('/')
            const urlBase = editingChannel.url.slice(0, splitIdx + 1)
            const originalKey = editingChannel.url.slice(splitIdx + 1)
            const trimmedLabel = editLabel.trim()
            const isTwitch = isTwitchChannel(editingChannel)
            const originalTwitchChat = editingChannel.twitch_chat_channel ?? ''
            const trimmedTwitchChat = editTwitchChatChannel.trim()
            const labelChanged =
              trimmedLabel !== editingChannel.label && trimmedLabel !== ''
            const keyChanged =
              editStreamKey !== originalKey && editStreamKey.trim() !== ''
            const twitchChatChanged =
              isTwitch && trimmedTwitchChat !== originalTwitchChat
            const nothingChanged =
              !labelChanged && !keyChanged && !twitchChatChanged

            const handleEditSave = (): void => {
              if (editingChannel === null) return
              if (updateChannel.isPending) return
              if (nothingChanged) {
                setEditingChannel(null)
                setEditLabel('')
                setEditStreamKey('')
                setEditTwitchChatChannel('')
                return
              }
              const input: {
                id: number
                label?: string
                url?: string
                twitch_chat_channel?: string
              } = { id: editingChannel.id }
              if (labelChanged) input.label = trimmedLabel
              if (keyChanged) input.url = urlBase + editStreamKey
              if (twitchChatChanged) input.twitch_chat_channel = trimmedTwitchChat
              updateChannel.mutate(input)
            }

            return (
              <>
                <DialogHeader>
                  <DialogTitle>Edit destination</DialogTitle>
                  <DialogDescription>
                    {`Change the name or stream key for this ${platformName(editingChannel)} destination. The ingest URL prefix stays fixed.`}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Ingest URL prefix</Label>
                    <div
                      data-testid="admin-channels-edit-url-base"
                      className="break-all rounded-md border border-border/60 bg-background/60 px-3 py-2 font-mono text-xs text-muted-foreground"
                    >
                      {urlBase}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="admin-channels-edit-label-input">Name</Label>
                    <Input
                      id="admin-channels-edit-label-input"
                      data-testid="admin-channels-edit-label"
                      type="text"
                      autoComplete="off"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="admin-channels-edit-stream-key-input">Stream key</Label>
                    <Input
                      id="admin-channels-edit-stream-key-input"
                      data-testid="admin-channels-edit-stream-key"
                      type="text"
                      autoComplete="off"
                      value={editStreamKey}
                      onChange={(e) => setEditStreamKey(e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                  {isTwitch && (
                    <div className="space-y-1.5">
                      <Label htmlFor="admin-channels-edit-twitch-chat-input">Twitch chat channel</Label>
                      <Input
                        id="admin-channels-edit-twitch-chat-input"
                        data-testid="admin-channels-edit-twitch-chat"
                        type="text"
                        autoComplete="off"
                        value={editTwitchChatChannel}
                        onChange={(e) => setEditTwitchChatChannel(e.target.value)}
                        placeholder="channel_login"
                      />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="ghost"
                    data-testid="admin-channels-edit-cancel"
                    onClick={() => {
                      setEditingChannel(null)
                      setEditLabel('')
                      setEditStreamKey('')
                      setEditTwitchChatChannel('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    data-testid="admin-channels-edit-save"
                    disabled={updateChannel.isPending || nothingChanged}
                    onClick={handleEditSave}
                  >
                    {updateChannel.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </DialogFooter>
              </>
            )
          })()}
        </DialogContent>
      </Dialog>
    </div>
  )
}
